import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { isDatabaseUnavailable } from 'services/database-unavailable-error';

/**
 * What the Edit Exercise page's `loadAsync` found. It is RETURNED rather than written into a
 * flag on the page, so that every exit has to say what happened: a plain `return;` does not
 * compile in a function typed to answer with one of these. Before that, some exits set a flag
 * and some returned silently, and the caller had to re-derive the same fact and guess which
 * card to draw — the split convention behind the never-ending spinner of #4737.
 *
 * It lives outside the page file so the rule below can be unit-tested without Angular.
 */
export enum ExerciseLoadOutcome {
  // there is an exercise on the page and the rest of the enter sequence may run
  Loaded,
  // the exercise is genuinely gone: deleted, or an id that was never in this collection
  NotFound,
  // the exercise could not be read, so we do not know whether it is gone
  Unreadable,
}

/**
 * Which card a failed load shows. Different things fail a load and the user must not be told
 * the same story about all of them. A not-found really does mean the exercise is gone. A
 * database that is unavailable means we could not look — saying "no longer available" there
 * could send the user off to recreate an exercise they still have.
 *
 * When the page read a CURATED exercise (fork-on-edit, admin), every other failure is a read
 * that did not work too: those reads throw it rather than answering "not in the catalog"
 * (df-c21e11979ef2), so it also gets the could-not-read card. The custom-exercise read keeps
 * the older answer, "not found", for such an error.
 */
export function loadOutcomeForFailure(err: unknown, readCuratedExercise: boolean): ExerciseLoadOutcome {
  if (isPouchNotFound(err)) {
    return ExerciseLoadOutcome.NotFound;
  }
  if (isDatabaseUnavailable(err) || readCuratedExercise) {
    return ExerciseLoadOutcome.Unreadable;
  }
  return ExerciseLoadOutcome.NotFound;
}
